import { stopAllMedia } from "@pathwright/media-utils"
import AlertBanner from "@pathwright/ui/src/components/alert/AlertBanner"
import useCardStackModal from "@pathwright/ui/src/components/card/useCardStackModal"
import ErrorBoundary from "@pathwright/ui/src/components/error/ErrorBoundary"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import LoadingCircle from "@pathwright/ui/src/components/loading/LoadingCircle"
import { useSingleCohortPathQuery } from "@pathwright/web/src/modules/api/generated"
import PathBlocksContentSyncer from "@pathwright/web/src/modules/blocks/PathBlocksContentSyncer"
import { CreateCohortCard } from "@pathwright/web/src/modules/cohort/create/CreateCohort"
import useCohortsQuery from "@pathwright/web/src/modules/cohort/switcher/useCohortsQuery"
import ResourceContext from "@pathwright/web/src/modules/path/ResourceContext"
import EditPath from "@pathwright/web/src/modules/path/edit/EditPath"
import LearnPath from "@pathwright/web/src/modules/path/learn/LearnPath"
import PreviewPath from "@pathwright/web/src/modules/path/preview/PreviewPath"
import SourcePathBlankSlate, {
  TeachModeBlankSlate,
  useGetShowBlankSlateTip
} from "@pathwright/web/src/modules/path/source-path/SourcePathBlankSlate"
import { SyncPlanContextProvider } from "@pathwright/web/src/modules/path/sync/SyncPlanContext"
import TeachPath from "@pathwright/web/src/modules/path/teach/TeachPath"
import usePathAnalytics from "@pathwright/web/src/modules/path/usePathAnalytics"
import { RESOURCE_TYPE_COLLECTION } from "@pathwright/web/src/modules/resource/constants"
import { flattenEdges } from "@pathwright/web/src/modules/utils/apollo"
import { ConnectStores } from "lib/core/store"
import { DESIGN, LEARN, PREVIEW, TEACH } from "path/constants"
import PropTypes from "prop-types"
import { useEffect, useState } from "react"
import Lesson from "../lesson/Lesson"
import StepSequence from "../step/StepSequence"
import "./ResourcePath.css"
import SyncPathReloader from "./SyncPlanReloader"
import PathControls from "./controls/PathControls"
import PathStatus from "./controls/PathStatus"
import PathViewAs from "./controls/PathViewAs"
import PathList from "./list/PathList"
import PostPathAnalytics from "./list/analytics/PostPathAnalytics"
import PathSync from "./state/PathSync"
import { getCohortCompletedMessage } from "./utils"
import withResourcePath from "./withResourcePath"

const ResourcePath = (props) => {
  const {
    loaded,
    path,
    pathProviderPath, // Path from usePath query, not Path store
    permissions,
    pathItems,
    mode,
    setMode,
    resource,
    offering,
    registration,
    progress,
    setAnalytics,
    pathId: contextPathId,
    cohortId,
    supportEmail
  } = props

  const [pathId, setPathId] = useState(contextPathId)

  useEffect(() => {
    // This comes from the Path store
    if (path) {
      setPathId(path?.id)
    }
  }, [path?.id])

  // Start with blocks content syncing if in Design mode
  // (this will be turned off when the syncing is done)
  const [blocksContentSyncing, setBlocksContentSyncing] = useState(
    mode === DESIGN
  )

  const { t, tc } = useTranslate()

  // We need to know about the Cohorts for this Resource
  // to determine if we need to show the Source Path blank slate
  const { cohorts, loading } = useCohortsQuery({
    resourceId: resource.id,
    skip: mode !== DESIGN && mode !== TEACH
  })

  // When we are a) On the Source path, b) in Design mode and c) there is only one cohort...
  //  ...we need to fetch the Path for that single cohort in order to render the SourcePathBlankSlate...
  //  ...which needs to know if that Path is empty
  const { data: singleCohortPath, loading: singleCohortPathLoading } =
    useSingleCohortPathQuery({
      variables: {
        cohort_id: cohorts?.[0]?.id
      },
      skip:
        !offering.is_curriculum ||
        mode !== DESIGN ||
        (cohorts?.length || 0) !== 1
    })

  const isPathLoading = !(
    (loaded && path && offering && offering.id === path.offering_id) // prevent showing for a brief second a stale path
  )

  const isDataLoading =
    loading || singleCohortPathLoading || blocksContentSyncing || isPathLoading

  // The Source Path will display a blank slate message
  //  if there are 0-1 Cohorts and the Source Path is empty
  //  and the message hasn't been dismissed;
  //  we also show a (different) Tip if there's a single Cohort but it has no Path items
  const needsBlankSlate = useGetShowBlankSlateTip({
    isEmptySource:
      mode === DESIGN &&
      offering?.is_curriculum &&
      !flattenEdges(pathProviderPath?.items)?.length,
    numCohorts: cohorts?.length || 0,
    singleCohortPath: singleCohortPath?.path,
    resource
  })

  // We use a state to control the display of the blank slate
  // b/c we want to be able to shut it off
  const [displayBlankSlate, setDisplayBlankSlate] = useState(null)

  useEffect(() => {
    // Once data is finished loading, set whether we need to render a blank slate.
    if (isDataLoading) {
      // Reset the blank slate when the dependencies change but data is loading.
      setDisplayBlankSlate(null)
    } else {
      setDisplayBlankSlate(needsBlankSlate)
    }
  }, [isDataLoading, needsBlankSlate, cohortId])

  // Since we're setting the blank slate in state, we must defer rendering
  // the path until the blank slate bool is set, otherwise we see a brief
  // flash of the path (albeit an empty path, but still jarring).
  const isLoading = displayBlankSlate === null || isDataLoading

  const { cardStack: createCohortCardStack, onOpenCardStack: onCreateCohort } =
    useCardStackModal({
      component: CreateCohortCard,
      props: {
        resourceId: resource?.id
      }
    })

  // Only retrieve analytics data when in Teach mode
  const analytics = usePathAnalytics(mode === TEACH ? offering : null)
  useEffect(() => {
    // update analyitcs in the pathAnalyticsStore
    setAnalytics(analytics)
  }, [analytics])

  useEffect(() => {
    // Ensure all media is stopped when step is navigated to
    if (props.params.step_id) {
      stopAllMedia()
    }
  }, [props.params.step_id])

  const completedCohortMessage = getCohortCompletedMessage(t)

  return (
    <ErrorBoundary>
      <SyncPlanContextProvider
        fromPathId={pathId}
        fromCohortId={cohortId}
        queryOptions={{
          skip: !(mode === DESIGN)
        }}
        path={pathProviderPath}
      >
        <ResourceContext.Provider
          value={{
            pathId: pathId,
            cohortId: cohortId,
            resource: resource ? { id: resource.id, slug: resource.slug } : {},
            group: offering
              ? {
                  id: offering.id,
                  is_curriculum: offering.is_curriculum
                }
              : {},
            registration: registration
              ? {
                  id: registration.id,
                  role: registration.role
                }
              : null
          }}
        >
          {completedCohortMessage ? (
            <AlertBanner.Banner
              message={completedCohortMessage}
              // href={`mailto:${supportEmail}`}
              icon="eye-lock"
              alertType={"info"}
            />
          ) : null}
          {/* This components ensure that path items with Blocks content have publish/modified dates */}
          {/* This will go away once the backend handles this. */}
          {mode === DESIGN ? (
            <PathBlocksContentSyncer
              onSync={(pathItems, syncMap) => setBlocksContentSyncing(false)}
              pathItems={pathProviderPath?.items}
              cohortId={cohortId}
            />
          ) : null}
          {mode === LEARN && permissions && permissions.can_access && (
            <PathStatus permissions={permissions} progress={progress} />
          )}
          <div className="ResourcePath">
            {!!offering && <PathControls disabled={displayBlankSlate} />}
            {isLoading ? (
              <div
                style={{
                  position: "absolute",
                  margin: "auto",
                  left: "0",
                  right: "0",
                  top: "50%",
                  transform: "translateY(-50%)",
                  textAlign: "center"
                }}
              >
                <LoadingCircle inverted={true} />
              </div>
            ) : (
              <div>
                {displayBlankSlate ? (
                  <SourcePathBlankSlate
                    cohort={cohorts?.[0]}
                    source={{
                      name: resource.name,
                      id: offering?.id,
                      resource
                    }}
                    cohorts={cohorts}
                    singleCohortPath={singleCohortPath?.path}
                    onDismiss={() => {
                      setDisplayBlankSlate(false)
                    }}
                    onLaunchCreateCohort={onCreateCohort}
                  />
                ) : mode === TEACH && !pathItems?.length ? (
                  <TeachModeBlankSlate onDismiss={() => setMode(DESIGN)} />
                ) : resource.resource_type === RESOURCE_TYPE_COLLECTION ? (
                  // collection path
                  !!offering && (
                    <>
                      {mode === DESIGN ? (
                        <EditPath cohort_id={cohortId} />
                      ) : mode === TEACH ? (
                        <TeachPath />
                      ) : mode === LEARN ? (
                        <LearnPath />
                      ) : mode === PREVIEW ? (
                        <PreviewPath />
                      ) : null}
                    </>
                  )
                ) : (
                  <PathList path={path} items={pathItems} />
                )}
                {mode === TEACH ? <PostPathAnalytics /> : undefined}
              </div>
            )}
          </div>
          {props.params.pathItemSourceId ? (
            props.params.pathItemType === "step" ? (
              <StepSequence {...props} />
            ) : (
              <Lesson {...props} />
            )
          ) : null}
          <PathSync />
          <PathViewAs />
          <SyncPathReloader />
          {mode === DESIGN ? createCohortCardStack : null}
        </ResourceContext.Provider>
      </SyncPlanContextProvider>
    </ErrorBoundary>
  )
}

const mapStateToProps = function (state, ownProps) {
  const { resource, offering, registration } = state.resourceStore.state
  const { path, mode, progress } = state.pathStore.state
  const { setMode } = state.pathStore.action
  const { setAnalytics } = state.pathAnalyticsStore.action
  const { support, pathwright_support_email } = state.supportStore.state
  let { items } = state.pathStore.state
  const permissions = state.resourceStore.request.permissions()

  let pathItems = items || []
  // Inject build components
  if (mode === DESIGN) {
    // Resource is lesson:
    // Add append control if no existing group.
    if ((resource != null ? resource.resource_type : undefined) === 20) {
      if (pathItems.length < 1) {
        pathItems.push({ item_type: "N" })
      }
      // Resoruce is course:
      // Add insert and append controls.
    } else {
      pathItems = []
      items = items || []
      for (let index = 0; index < items.length; index++) {
        // Add "insert" component before every list item
        const item = items[index]
        if (index !== 0) {
          pathItems.push({ item_type: "I" })
        }
        pathItems.push(item)
      }
      // Add "append" component at the end of the path (beginning if path is empty)
      pathItems.push({ item_type: "N" })
    }
  }

  const props = _.assign(
    {},
    {
      path,
      pathItems,
      permissions,
      resource,
      mode,
      setMode,
      offering,
      registration,
      progress,
      setAnalytics,
      supportEmail: support.support_email || pathwright_support_email
    },
    ownProps
  )

  return props
}

const stores = [
  {
    store: "path",
    watch: ["path", "mode", "progress", "viewing_as_learner", "next_step"]
  },
  { store: "resource", watch: ["resource", "offering", "registration"] },
  {
    store: "auth",
    watch: ["is_authenticated"]
  },
  {
    store: "pathAnalytics",
    watch: false
  },
  {
    store: "support"
  }
]

ResourcePath.displayName = "ResourcePath"
ResourcePath.contextTypes = { mode: PropTypes.number }
const ResourcePathConnected = ConnectStores(
  ResourcePath,
  stores,
  {},
  mapStateToProps
)

ResourcePathConnected.displayName = "ResourcePathConnected"

export default withResourcePath(ResourcePathConnected)
